import { ChangeEvent, FunctionComponent } from 'react';

// Libs
import * as Yup from 'yup';
import { useFormik } from 'formik';
import classnames from 'classnames';

// Contexts
import { useAlertContext } from '@Components/Alert/Context';
import { useViewDetected } from '@Components/Utils/ViewDetected';

// Components
import Input from '@Components/UI/Form/Input';
import Radio from '@Components/UI/Form/Radio';
import ButtonLoading from '@Components/UI/ButtonLoading';

// Apis
import { phoneRegExp, MESSAGE_SUCCESS, MESSAGE_ERROR, useSupportAirTable, CallbackDataType } from '@Libs/utils';

// Styles
import styles from './Form.module.scss';

interface FormProps {
    className?: string;
}

type FieldItem = {
    name: string;
    key: string;
};

const AIRTABLE_FIELDS: Array<FieldItem> = [
    { name: 'Name', key: 'name' },
    { name: 'Email', key: 'email' },
    { name: 'Công việc', key: 'work' },
    { name: 'Thời gian rảnh', key: 'timeFree' },
    { name: 'Khu vực công tác', key: 'address' },
    { name: 'Số điện thoại', key: 'phoneNumber' },
    { name: 'Thời gian ký hợp tác', key: 'signed' },
    { name: 'Chứng chỉ hành nghề', key: 'certificate' },
];

const BASE_ID = 'apprV0ryUoLMqsih1';
const BASE_FORM = 'Form hợp tác CTV riêng';

const validationSchema = Yup.object({
    phoneNumber: Yup.string()
        .required('Vui lòng điền đầy đủ thông tin!')
        .min(9, 'Vui lòng nhập số điện thoại hợp lệ!')
        .max(10, 'Vui lòng nhập số điện thoại hợp lệ!')
        .matches(phoneRegExp, 'Vui lòng nhập số điện thoại hợp lệ!'),
    email: Yup.string().email('Vui lòng nhập email hợp lệ!'),
    name: Yup.string().required('Vui lòng điền đầy đủ thông tin!'),
    work: Yup.string().required('Vui lòng điền đầy đủ thông tin!'),
    signed: Yup.string().required('Vui lòng điền đầy đủ thông tin!'),
    address: Yup.string().required('Vui lòng điền đầy đủ thông tin!'),
    timeFree: Yup.string().required('Vui lòng điền đầy đủ thông tin!'),
    certificate: Yup.string().required('Vui lòng điền đầy đủ thông tin!'),
});

const Form: FunctionComponent<FormProps> = ({ className }) => {
    const { alert } = useAlertContext();
    const { deviceType } = useViewDetected();

    const isMobile = deviceType !== 'desktop';

    const { loading, sendAirTable } = useSupportAirTable(BASE_ID);

    const registerForm = (values, action) => {
        const fields = AIRTABLE_FIELDS.reduce((fieldObj, fieldItem: FieldItem) => {
            fieldObj[fieldItem.name] = values[fieldItem.key];
            return fieldObj;
        }, {});

        sendAirTable(BASE_FORM, fields, '', (data: CallbackDataType) => {
            if (data.type === 'success') {
                alert({
                    type: 'success',
                    text: MESSAGE_SUCCESS,
                    title: 'Gửi thành công',
                });
                action.resetForm();
            } else {
                alert({
                    type: 'error',
                    text: MESSAGE_ERROR,
                    title: 'Đã xảy ra lỗi',
                });
            }
        });
    };

    const { errors, values, setFieldValue, handleSubmit, isValid } = useFormik({
        validationSchema,
        initialValues: {
            name: '',
            work: '',
            email: '',
            signed: '',
            address: '',
            timeFree: '',
            phoneNumber: '',
            certificate: '',
        },
        onSubmit: registerForm,
    });

    const changeValue = (event: ChangeEvent<HTMLInputElement>) => {
        const { value, name } = event.target;

        setFieldValue(name, value.trim(), !!errors[name]);
    };

    const wrapperClassName = classnames([
        styles.wrapper,
        {
            [styles.mobile]: isMobile,
        },
        className,
    ]);

    return (
        <form className={wrapperClassName} onSubmit={handleSubmit}>
            <div className={styles.title}>Đăng ký Cộng tác viên</div>

            <Input
                name="name"
                requiredMark
                label={'Họ tên'}
                value={values.name}
                error={!!errors.name}
                onChange={changeValue}
                errorMessage={errors.name}
                labelClassName={styles.label}
                className={styles.input_field}
                errorClassName={styles.error_message}
                labelFloatClassName={styles.label_focus}
            />

            <Input
                requiredMark
                name="phoneNumber"
                onChange={changeValue}
                value={values.phoneNumber}
                error={!!errors.phoneNumber}
                labelClassName={styles.label}
                className={styles.input_field}
                errorMessage={errors.phoneNumber}
                label="Nhập số điện thoại liên hệ"
                errorClassName={styles.error_message}
                labelFloatClassName={styles.label_focus}
            />

            <Input
                name="email"
                value={values.email}
                onChange={changeValue}
                label="Nhập email liên hệ"
                errorMessage={errors.email}
                labelClassName={styles.label}
                className={styles.input_field}
                errorClassName={styles.error_message}
                labelFloatClassName={styles.label_focus}
            />

            <Input
                requiredMark
                name="address"
                onChange={changeValue}
                value={values.address}
                error={!!errors.address}
                errorMessage={errors.address}
                labelClassName={styles.label}
                className={styles.input_field}
                errorClassName={styles.error_message}
                labelFloatClassName={styles.label_focus}
                label="Tỉnh/Thành, Quận/Huyện có thể công tác"
            />

            <div className={styles.radio_wrapper}>
                <div className={styles.radio_title}>
                    Công việc hiện tại của bạn là gì ? <span>*</span>
                </div>

                <Radio
                    name="work"
                    value="Điều dưỡng"
                    label="Điều dưỡng"
                    onChange={changeValue}
                    className={styles.radio}
                    checked={values.work === 'Điều dưỡng'}
                />

                <Radio
                    name="work"
                    onChange={changeValue}
                    label="KTV xét nghiệm"
                    value="KTV xét nghiệm"
                    className={styles.radio}
                    checked={values.work === 'KTV xét nghiệm'}
                />

                <Radio
                    name="work"
                    label="Y sĩ"
                    value="Y sĩ"
                    onChange={changeValue}
                    className={styles.radio}
                    checked={values.work === 'Y sĩ'}
                />

                <Radio
                    name="work"
                    label="Bác sĩ"
                    value="Bác sĩ"
                    onChange={changeValue}
                    className={styles.radio}
                    checked={values.work === 'Bác sĩ'}
                />

                <Radio
                    name="work"
                    label="Mục khác"
                    value="Mục khác"
                    onChange={changeValue}
                    className={styles.radio}
                    checked={values.work === 'Mục khác'}
                />
            </div>

            {errors.work && (
                <div className={styles.error_message}>
                    <br />
                    {errors.work}
                </div>
            )}

            <div className={styles.radio_wrapper}>
                <div className={styles.radio_title}>
                    Bạn đã có chứng chỉ hành nghề y chưa ? (CCHN) <span>*</span>
                </div>

                <Radio
                    label="Đã có"
                    value="Đã có"
                    name="certificate"
                    onChange={changeValue}
                    className={styles.radio}
                    checked={values.certificate === 'Đã có'}
                />

                <Radio
                    label="Chưa có"
                    value="Chưa có"
                    name="certificate"
                    onChange={changeValue}
                    className={styles.radio}
                    checked={values.certificate === 'Chưa có'}
                />
            </div>

            {errors.certificate && (
                <div className={styles.error_message}>
                    <br />
                    {errors.certificate}
                </div>
            )}

            <div className={styles.radio_wrapper}>
                <div className={styles.radio_title}>
                    Bạn có thời gian rảnh buổi sáng không ? <span>*</span>
                </div>
                <Radio
                    label="Có"
                    value="Có"
                    name="timeFree"
                    onChange={changeValue}
                    className={styles.radio}
                    checked={values.timeFree === 'Có'}
                />

                <Radio
                    label="Không"
                    value="Không"
                    name="timeFree"
                    onChange={changeValue}
                    className={styles.radio}
                    checked={values.timeFree === 'Không'}
                />

                <Radio
                    name="timeFree"
                    onChange={changeValue}
                    className={styles.radio}
                    label="Tùy ca trực theo lịch làm việc"
                    value="Tùy ca trực theo lịch làm việc"
                    checked={values.timeFree === 'Tùy ca trực theo lịch làm việc'}
                />

                <Radio
                    name="timeFree"
                    label="Mục khác"
                    value="Mục khác"
                    onChange={changeValue}
                    className={styles.radio}
                    checked={values.timeFree === 'Mục khác'}
                />
            </div>

            {errors.timeFree && (
                <div className={styles.error_message}>
                    <br />
                    {errors.timeFree}
                </div>
            )}

            <div className={styles.radio_wrapper}>
                <div className={styles.radio_title}>
                    Bạn có thể tham gia ký hợp tác vào thời gian nào trong tuần ? <span>*</span>
                </div>

                <Radio
                    name="signed"
                    label="Thứ 3 (15h00)"
                    value="Thứ 3 (15h00)"
                    onChange={changeValue}
                    className={styles.radio}
                    checked={values.signed === 'Thứ 3 (15h00)'}
                />

                <Radio
                    name="signed"
                    label="Thứ 5 (15h00)"
                    value="Thứ 5 (15h00)"
                    onChange={changeValue}
                    className={styles.radio}
                    checked={values.signed === 'Thứ 5 (15h00)'}
                />
            </div>

            {errors.signed && (
                <div className={styles.error_message}>
                    <br />
                    {errors.signed}
                </div>
            )}

            <ButtonLoading loading={loading} disabled={!isValid} type="submit" className={styles.btn_submit}>
                Gửi yêu cầu
            </ButtonLoading>
        </form>
    );
};

Form.defaultProps = {
    className: '',
};

export default Form;
